"use client";

import { cn } from "@ui/lib/utils";
import { RepeatIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";

import { openSoundEffectEditor, toggleEditMode, toggleSoundLoop } from "../state/actions";
import { useSoundboard } from "../state/reducer";
import { KEYBOARD_MAPPING, type Sound } from "../types";
import { useAudio } from "./AudioPlayer";
import { MidiListener } from "./MidiListener";
import { SoundBoardButton } from "./SoundBoardButton";

// Helper hook to get sound states
const useSoundStates = (soundUrl: string) => {
  const { state } = useSoundboard();
  return useMemo(
    () => ({
      isPlaying: state.playingSounds[soundUrl],
    }),
    [state.playingSounds, soundUrl],
  );
};

const ButtonDot = ({ isActive, id, className }: { className?: string; id: string; isActive: boolean }) => {
  return (
    <div
      className={cn(
        "sb-color-transparent flex h-24 w-24 items-center justify-center rounded-full text-xs uppercase transition-colors duration-100",
        isActive ? "sb-bg-orange sb-glow" : "sb-bg-gray",
        className,
      )}
    >
      <span>{id}</span>
    </div>
  );
};

const SoundButton = ({ sound, buttonIndex, id }: { buttonIndex: number; id: string; sound: Sound }) => {
  const { queueSound } = useAudio();
  const { isPlaying } = useSoundStates(sound.url);
  const { state, dispatch } = useSoundboard();
  const isBeingEdited = state.isInEditMode && state.panel?.data?.buttonIndex === buttonIndex;

  const handleClick = () => {
    if (state.isInEditMode) {
      dispatch(openSoundEffectEditor(buttonIndex, state));
    } else if (state.isInLoopEditMode) {
      dispatch(toggleSoundLoop({ soundId: sound.id }));
      !isPlaying && queueSound(sound.id);
    } else {
      queueSound(sound.id);
    }
  };

  return (
    <SoundBoardButton className={cn("sound-button group")} onClick={handleClick}>
      {sound.isLooped && <RepeatIcon className='absolute left-8 top-8 text-lightest' size={14} />}
      <ButtonDot id={id} isActive={isPlaying || isBeingEdited} />
      <p className='font-soundboard f-ui-04 w-full truncate text-lightest'>{sound.name}</p>
    </SoundBoardButton>
  );
};

const EmptySoundButton = ({ buttonIndex, id }: { buttonIndex: number; id: string }) => {
  const { dispatch, state } = useSoundboard();
  const t = useTranslations("ClientKeys.soundboard");
  const handleClick = () => {
    if (!state.isInEditMode) {
      dispatch(toggleEditMode());
    }
    dispatch(openSoundEffectEditor(buttonIndex, state));
  };
  const isBeingEdited = state.panel?.data?.buttonIndex === buttonIndex;

  return (
    <SoundBoardButton className={cn("sound-button group")} disabled={state.isInLoopEditMode} onClick={handleClick}>
      <ButtonDot id='' isActive={isBeingEdited} />
      <p className='font-soundboard f-ui-04 w-full truncate text-lightest'>{t("button_grid.empty_button_label")}</p>
    </SoundBoardButton>
  );
};

const SoundboardKeypressListener = () => {
  const { state } = useSoundboard();
  const { queueSound } = useAudio();

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (state.isInEditMode || !!state.panel) return;

      const keyIndex = KEYBOARD_MAPPING.indexOf(event.key.toLowerCase());
      if (keyIndex !== -1) {
        const sound = state.presets[state.activePresetId!]?.sounds[keyIndex];
        if (sound && sound.type === "sound") {
          queueSound(sound.id);
        }
      }
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
    };
  }, [queueSound, state]);

  return null;
};

const LoadingSoundButton = () => {
  return (
    <SoundBoardButton className='sound-button' disabled>
      <div className='h-20 w-20 animate-pulse rounded-full bg-darker transition-colors duration-200' />
    </SoundBoardButton>
  );
};

export const ButtonGrid = () => {
  const { state } = useSoundboard();
  const activePreset = state.presets[state.activePresetId!];

  return (
    <div className='grid grid-cols-4 gap-4'>
      <SoundboardKeypressListener />
      <MidiListener />
      {(!activePreset?.sounds?.length || !activePreset?.loaded) &&
        Array.from({ length: 16 }).map((_, index) => <LoadingSoundButton key={index} />)}
      {activePreset?.loaded &&
        activePreset?.sounds.map((sound, index) => {
          const id = KEYBOARD_MAPPING[index]?.toString() ?? `${index + 1}`;
          return sound.type === "sound" ? (
            <SoundButton buttonIndex={index} id={id} key={index} sound={sound} />
          ) : (
            <EmptySoundButton buttonIndex={index} id={id} key={index} />
          );
        })}
    </div>
  );
};
